// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { SelectChangeEvent } from '@mui/material/Select';
import { isGerminationStep, isPlantStep, isProductionStep } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as React from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '../../../lib/base';
import { sortTasksByName } from '../../../utils/sortTasks';
import InternalDatePicker from '../date/InternalDatePicker';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export const getNewSelectedValues = (prevValues: string[], selectedValues: string[]): string[] => {
  const prevHasAll = prevValues.includes(ALL_ITEMS);
  // If "All" is already selected, and a new item is selected, then remove "All" and keep the new item
  if (prevHasAll && selectedValues.length > 1) {
    return selectedValues.filter((value) => value !== ALL_ITEMS);
  }
  // If "All" is selected, then clear the selected values
  if (!prevHasAll && selectedValues.includes(ALL_ITEMS)) {
    return [ALL_ITEMS];
  }
  // If there are no values selected, then select "All"
  if (selectedValues.length === 0) {
    return [ALL_ITEMS];
  }

  return selectedValues;
};

export function filterTasksByName<T>({
  tasks,
  filterValue,
  getTaskName,
}: {
  tasks: T[];
  filterValue: string[];
  getTaskName: (task: T) => string;
}): T[] {
  if (filterValue.includes(ALL_ITEMS)) {
    return tasks;
  }
  const result: T[] = [];
  if (filterValue.includes(NON_PRODUCTION_TYPES)) {
    result.push(...tasks.filter((task) => !isProductionStep(getTaskName(task))));
  }
  if (filterValue.includes(ALL_PLANT_TYPES)) {
    result.push(...tasks.filter((task) => isPlantStep(getTaskName(task))));
  }
  if (filterValue.includes(ALL_GERMINATION_TYPES)) {
    result.push(...tasks.filter((task) => isGerminationStep(getTaskName(task))));
  }
  result.push(...tasks.filter((task) => filterValue.includes(getTaskName(task))));

  return result;
}

const truncate = (text: string, length: number) => ({
  text: text.length > length ? `${text.slice(0, length)}...` : text,
  isTuncated: text.length > length,
});

function renderValue(selected: string[]) {
  const labels = selected.map((selectedValue) => truncate(selectedValue, 10));
  if (labels.length > 3) {
    const slicedLabels = labels.slice(0, 3);
    const labelsText = slicedLabels.map((label) => label.text);
    const lastIsTruncated = slicedLabels[slicedLabels.length - 1].isTuncated;
    const labelsTextWithEllipsis = labelsText.join(', ');

    return `${labelsTextWithEllipsis}${lastIsTruncated ? '' : '...'}`;
  }

  return labels.map((label) => label.text).join(', ');
}

export const makeMultiSelectFilter = ({
  value,
  onChangeValue,
  label,
  id,
  options,
}: {
  readonly value: string[];
  readonly onChangeValue: (e: SelectChangeEvent<string[]>) => void;
  readonly label: string;
  readonly id: string;
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
}) => {
  // Create a Map for value to label mapping
  const valueToLabelMap = new Map(options.map(({ value, label }) => [value, label]));

  return (
    <FormControl sx={{ minWidth: 140 }} size="small" key={id}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        multiple
        name={`${label} Input`}
        id={id}
        value={value}
        label={label}
        onChange={onChangeValue}
        renderValue={(selected) => {
          const labels = selected.map((selectedValue) =>
            truncate(valueToLabelMap.get(selectedValue) ?? selectedValue, 10),
          );
          if (labels.length > 3) {
            const slicedLabels = labels.slice(0, 3);
            const labelsText = slicedLabels.map((label) => label.text);
            const lastIsTruncated = slicedLabels[slicedLabels.length - 1].isTuncated;
            const labelsTextWithEllipsis = labelsText.join(', ');

            return `${labelsTextWithEllipsis}${lastIsTruncated ? '' : '...'}`;
          }

          return labels.map((label) => label.text).join(', ');
        }}
      >
        <MenuItem value={ALL_ITEMS}>{ALL_ITEMS}</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const makeSelectFilter = ({
  value,
  onChangeValue,
  label,
  id,
  options,
}: {
  readonly value: string;
  readonly onChangeValue: (e: SelectChangeEvent<string>) => void;
  readonly label: string;
  readonly id: string;
  readonly options: { readonly value: string; readonly label: string }[];
}) => {
  return (
    <FormControl sx={{ minWidth: 140 }} size="small" key={id}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select id={id} value={value} name={`${label} Input`} label={label} onChange={onChangeValue}>
        <MenuItem value={ALL_ITEMS}>{ALL_ITEMS}</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const ALL_PLANT_TYPES = 'All Plant Tasks';
export const ALL_GERMINATION_TYPES = 'All Germination Tasks';
export const NON_PRODUCTION_TYPES = 'All Non-Production Tasks';
export const ALL_ITEMS = 'All';

export const makeNameFilter = <T,>({
  value,
  onChangeValue,
  objects,
  getName,
  isForTasks = false,
  label = 'Name Filter',
}: {
  readonly value: string[];
  readonly onChangeValue: (e: SelectChangeEvent<string[]>) => void;
  readonly objects: readonly T[];
  readonly getName: (obj: T) => string;
  readonly isForTasks?: boolean;
  readonly label?: string;
}) => {
  const names = new Set(
    objects.map((t) => getName(t)).filter((t) => (isForTasks ? isProductionStep(t) : true)),
  ).values();
  const namesSorted = [...names];
  namesSorted.sort(sortTasksByName);

  const id = 'task-name-filter';

  return (
    <FormControl sx={{ minWidth: 140 }} size="small" key={id}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        multiple
        name={`${label} Input`}
        id={id}
        value={value}
        label={label}
        onChange={onChangeValue}
        renderValue={renderValue}
      >
        <MenuItem value={ALL_ITEMS}>{ALL_ITEMS}</MenuItem>
        {isForTasks && <MenuItem value={ALL_PLANT_TYPES}>{ALL_PLANT_TYPES}</MenuItem>}
        {isForTasks && <MenuItem value={ALL_GERMINATION_TYPES}>{ALL_GERMINATION_TYPES}</MenuItem>}
        {isForTasks && <MenuItem value={NON_PRODUCTION_TYPES}>{NON_PRODUCTION_TYPES}</MenuItem>}
        {namesSorted.map((stepName) => (
          <MenuItem key={stepName} value={stepName}>
            {stepName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const DATE_DROPDOWN_FILTER_FMT = 'ddd, MMM D';
export const makeDateDropdownFilter = <T,>({
  value,
  onChangeValue,
  objects,
  getTime,
  userTimezone,
}: {
  readonly value: string[];
  readonly onChangeValue: (e: SelectChangeEvent<string[]>) => void;
  readonly objects: readonly T[];
  readonly getTime: (obj: T) => number;
  readonly userTimezone: string;
}) => {
  const timesSet = new Set(objects.map((t) => getTime(t)));
  const timesFormatted = Array.from(timesSet).map((t) => ({
    time: t,
    formattedTime: dayjs.unix(t).tz(userTimezone).format(DATE_DROPDOWN_FILTER_FMT),
  }));
  const timesSorted = [...timesFormatted];
  timesSorted.sort((a, b) => a.time - b.time);

  const id = 'task-date-filter';

  return (
    <FormControl sx={{ minWidth: 140 }} size="small" key={id}>
      <InputLabel id={`${id}-label`}>Date Filter</InputLabel>
      <Select
        multiple
        name={'Date Filter Input'}
        id={id}
        value={value}
        label="Date Filter"
        onChange={onChangeValue}
        renderValue={renderValue}
      >
        <MenuItem value={ALL_ITEMS}>{ALL_ITEMS}</MenuItem>
        {timesSorted.map(({ formattedTime: date }) => (
          <MenuItem key={date} value={date}>
            {date}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const makeDatePickerFilter = ({
  value,
  onChangeValue,
  minDate,
  maxDate,
  label,
}: {
  readonly value: dayjs.Dayjs | null;
  readonly onChangeValue: (val: dayjs.Dayjs | null) => void;
  readonly minDate?: dayjs.Dayjs;
  readonly maxDate?: dayjs.Dayjs;
  readonly label: string;
}) => {
  return (
    <InternalDatePicker
      formatDensity="dense"
      minDate={minDate}
      maxDate={maxDate}
      label={label}
      value={value}
      onChange={onChangeValue}
      textFieldProps={{
        size: 'small',
        slotProps: {
          inputLabel: {
            shrink: true,
          },
        },
        sx: {
          width: '170px',
        },
      }}
    />
  );
};
